/**
 * getDefaultStartDate
 *
 * Returns the trainer's "smart default" Start date for a program assignment.
 *
 * Rule:
 *   - If today is Monday → today (because "next Monday" 7 days out is wrong;
 *     the trainer almost always means "starting this week").
 *   - Otherwise → the next Monday.
 *
 * Returns a Date at local-midnight so it round-trips through `format(d, 'yyyy-MM-dd')`
 * without timezone drift (which would land us on the wrong day for trainers
 * west of UTC).
 *
 * Used by the 3 program-assignment dialogs (single, after-save, bulk) so
 * trainers always start with a sensible Monday and don't have to think about it.
 */
export function getDefaultStartDate(now: Date = new Date()): Date {
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, … 6 = Saturday
  // Days to add to land on the next Monday. If today IS Monday, return today (0).
  const daysUntilMonday = dayOfWeek === 1 ? 0 : (8 - dayOfWeek) % 7 || 7;
  // (8 - dayOfWeek) % 7 gives:
  //   Sun (0) → 1, Mon (1) → 0, Tue (2) → 6, Wed (3) → 5, Thu (4) → 4,
  //   Fri (5) → 3, Sat (6) → 2.
  // The `|| 7` fallback is defensive — Mon is special-cased above so it
  // never hits the `% 7 === 0` branch via this path.

  const result = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  result.setDate(result.getDate() + daysUntilMonday);
  return result;
}
